import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {LimitedPortalProperties, PortalApi} from "components/src/api/portal/generated";
import {RenderAlert} from "components/src/common/RenderAlert";
import {PortalDashboardContextProvider} from "components/src/context/PortalDashboardContextProvider";
import {usePortalDashboardContext} from "components/src/hook/UsePortalDashboardContext";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {Home as DashboardIcon, LogIn as LoginActivityIcon, Settings as SettingsIcon} from "iconoir-react";
import React, {useEffect, useState} from "react";
import {Card, CardGroup} from "react-bootstrap";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {DashboardTable} from "./DashboardTable";
import {DashboardTableRow} from "./DashboardTableRow";

/*
 * REST api's for accessing the back-end
 */
const portalApi = new PortalApi(ApiConfig.config());

type LoginActivityEntry = {
    r: string; // remote address
    t: number; // timestamp
};

interface PortalDashboardProps {
    beforeContent?: React.ReactNode;
}

const PortalDashboardContent = ({beforeContent}: PortalDashboardProps) =>
{
    /**
     * The total number of messages
     */
    const [limitedPortalProperties, setLimitedPortalProperties] = useState<LimitedPortalProperties | undefined>(undefined);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Portal dashboard context
     */
    const {stateCounter, errorMessage, setErrorMessage} = usePortalDashboardContext();

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        portalApi.portalControllerGetPortalProperties().then(response => {
                setLimitedPortalProperties(response.data)
            })
            .catch((err: Error | AxiosError) => {
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

    }, [setSpinnerStateCounter,
        stateCounter,
        setErrorMessage,
        t]);

    const renderLoginActivity = () =>
    {
        if (limitedPortalProperties?.loginActivityJSON === undefined) {
            return null;
        }

        const data = JSON.parse(limitedPortalProperties.loginActivityJSON) as LoginActivityEntry[];

        return data.map((entry, index) => (
            <tr key={index}>
                <td>{entry.r}</td>
                <td>{DateUtils.fromTimestampToLocaleDateStringShort(entry.t)}</td>
            </tr>
        ))
    }

    const {setStateCounter} = usePortalDashboardContext();

    return (
        <>
            <PageHeader icon={<DashboardIcon />} title={t("portal-dashboard-title")} onClickReload={() => setStateCounter(getStateCounter => getStateCounter + 1)}/>


            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <PageContent>
                {beforeContent}

                <Row className="mt-2">
                    <CardGroup>
                        <Card className="me-4">
                            <Card.Header><SettingsIcon className="text-success"/> {t('portal-dashboard-settings')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('portal-dashboard-attachments-max-size')}
                                                           value={Utils.formatBytes(limitedPortalProperties?.replyAttachmentsMaxSize)}
                                                           className="text-success"/>
                                        <DashboardTableRow name={t('portal-dashboard-body-max-size')}
                                                           value={Utils.formatBytes(limitedPortalProperties?.replyBodyMaxSize)}
                                                           className="text-success"/>
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="border-start">
                            <Card.Header><LoginActivityIcon className="text-success"/> {t('portal-dashboard-login-activity')}</Card.Header>
                            <Card.Body>
                                <Table hover responsive size="sm">
                                    <colgroup>
                                        <col className="col-md-2"/>
                                        <col className="col-md-10"/>
                                    </colgroup>
                                    <thead className="sticky-top">
                                    <tr className="text-nowrap">
                                        <th>{t('portal-dashboard-login-activity-remote-address')}</th>
                                        <th>{t('portal-dashboard-login-activity-date')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {renderLoginActivity()}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </CardGroup>
                </Row>

                <Row className="mt-2">
                    {t(['portal-dashboard-custom-content']).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal-dashboard-custom-content"]))} />}
                </Row>
            </PageContent>
        </>
    )
}

export const PortalDashboard = ({beforeContent}: PortalDashboardProps) =>
{
    return (
        <PortalDashboardContextProvider>
            <PortalDashboardContent beforeContent={beforeContent}/>
        </PortalDashboardContextProvider>
    );
}
